import { SplitMode } from "./splitPageUtils";

export interface PaneMetrics {
  paneIndex: number;
  scrollTop: number;
  clientHeight: number;
  scrollHeight: number;
  pageNumber?: number;
}

export interface RowMetrics {
  index: number;
  top: number;
  height: number;
  bottom: number;
  header?: string;
  chords?: string;
  text?: string;
  tab?: string[];
}

export interface SplitIntersectionIndicator {
  paneIndex: number;
  targetPaneIndex: number;
  role: "outgoing" | "incoming";
  rowIndex: number;
  pairId: string;
  colorIndex: number;
  label: string;
  rowTop: number;
  rowHeight: number;
  viewportTop: number;
  isVisible: boolean;
}

export interface SplitIntersectionPair {
  id: string;
  sourcePaneIndex: number;
  targetPaneIndex: number;
  colorIndex: number;
  outgoing: SplitIntersectionIndicator;
  incoming: SplitIntersectionIndicator;
}

/**
 * Pure helper to estimate or extract row geometric metrics.
 * Supports explicit row heights/tops or estimates based on content and font scale.
 */
export function calculateRowMetrics(
  rows: any[],
  fontScale: number = 1
): RowMetrics[] {
  if (!rows || rows.length === 0) {
    return [];
  }

  const scale = Math.max(0.2, fontScale || 1);
  let currentTop = 0;
  const metrics: RowMetrics[] = [];

  for (let i = 0; i < rows.length; i++) {
    const row = rows[i];
    let rowHeight = 0;

    if (typeof row?.height === "number") {
      rowHeight = row.height;
    } else {
      const baseLineHeight = Math.round(26 * scale);
      let contentLines = 0;

      if (row?.header) contentLines += 1;
      if (row?.chords) contentLines += 1;
      if (row?.text) contentLines += 1;
      if (Array.isArray(row?.tab) && row.tab.length > 0) {
        contentLines += row.tab.length;
      }

      if (contentLines === 0) {
        contentLines = 1;
      }

      const rowMargin = Math.round(8 * scale);
      rowHeight = contentLines * baseLineHeight + rowMargin;
    }

    const explicitTop = typeof row?.top === "number" ? row.top : currentTop;
    const bottom = explicitTop + rowHeight;

    metrics.push({
      index: i,
      top: explicitTop,
      height: rowHeight,
      bottom,
      header: row?.header,
      chords: row?.chords,
      text: row?.text,
      tab: row?.tab,
    });

    currentTop = bottom;
  }

  return metrics;
}

/**
 * Compares two panes to determine logical reading order.
 * Priority: pageNumber (if present) -> scrollTop -> paneIndex.
 */
function comparePanes(a: PaneMetrics, b: PaneMetrics): number {
  if (
    a.pageNumber !== undefined &&
    b.pageNumber !== undefined &&
    a.pageNumber !== b.pageNumber
  ) {
    return a.pageNumber - b.pageNumber;
  }
  if (a.scrollTop !== b.scrollTop) {
    return a.scrollTop - b.scrollTop;
  }
  return a.paneIndex - b.paneIndex;
}

/**
 * Pure geometry function to calculate handover pairs between split panes.
 */
export function calculateSplitIntersectionPairs(
  panes: PaneMetrics[],
  rows: RowMetrics[],
  splitMode: SplitMode
): SplitIntersectionPair[] {
  if (splitMode <= 1 || !panes || panes.length < 2 || !rows || rows.length === 0) {
    return [];
  }

  const activePanes = panes.filter((p) => p && p.paneIndex < splitMode);
  if (activePanes.length < 2) {
    return [];
  }

  const sortedPanes = [...activePanes].sort(comparePanes);
  const pairs: SplitIntersectionPair[] = [];

  for (let i = 0; i < sortedPanes.length - 1; i++) {
    const sourcePane = sortedPanes[i];
    const targetPane = sortedPanes[i + 1];

    const sourceTop = Math.max(0, sourcePane.scrollTop);
    const sourceBottom = sourceTop + sourcePane.clientHeight;
    const targetTop = Math.max(0, targetPane.scrollTop);
    const targetBottom = targetTop + targetPane.clientHeight;

    const sourceVisible = rows.filter(
      (r) => r.bottom > sourceTop && r.top < sourceBottom
    );
    const targetVisible = rows.filter(
      (r) => r.bottom > targetTop && r.top < targetBottom
    );

    let outgoingRow: RowMetrics;
    let incomingRow: RowMetrics;

    if (sourceVisible.length === 0 && targetVisible.length === 0) {
      outgoingRow = rows[0];
      incomingRow = rows[Math.min(1, rows.length - 1)];
    } else if (sourceVisible.length === 0) {
      incomingRow = targetVisible[0];
      outgoingRow = rows[Math.max(0, incomingRow.index - 1)];
    } else if (targetVisible.length === 0) {
      outgoingRow = sourceVisible[sourceVisible.length - 1];
      incomingRow = rows[Math.min(rows.length - 1, outgoingRow.index + 1)];
    } else {
      const overlap = sourceVisible.filter((sr) =>
        targetVisible.some((tr) => tr.index === sr.index)
      );

      if (overlap.length > 0) {
        // Filter for overlap rows that have at least 16px (or half their height) visible in sourcePane
        const comfortablyVisible = overlap.filter(
          (r) => (r.top - sourceTop) + Math.min(r.height, 16) <= sourcePane.clientHeight
        );
        const sharedRow = comfortablyVisible.length > 0
          ? comfortablyVisible[comfortablyVisible.length - 1]
          : overlap[overlap.length - 1];
        outgoingRow = sharedRow;
        incomingRow = sharedRow;
      } else {
        // Contiguous or non-overlap handover
        outgoingRow = sourceVisible[sourceVisible.length - 1];
        incomingRow = targetVisible[0];
      }
    }

    const pairId = `split-pair-${sourcePane.paneIndex}-${targetPane.paneIndex}`;
    const colorIndex = i;

    const outgoingViewportTop = outgoingRow.top - sourceTop;
    const isOutgoingVisible =
      outgoingViewportTop + outgoingRow.height > 0 &&
      outgoingViewportTop < sourcePane.clientHeight;

    const incomingViewportTop = incomingRow.top - targetTop;
    const isIncomingVisible =
      incomingViewportTop + incomingRow.height > 0 &&
      incomingViewportTop < targetPane.clientHeight;

    const outgoing: SplitIntersectionIndicator = {
      paneIndex: sourcePane.paneIndex,
      targetPaneIndex: targetPane.paneIndex,
      role: "outgoing",
      rowIndex: outgoingRow.index,
      pairId,
      colorIndex,
      label: `Continue in Col ${targetPane.paneIndex + 1}`,
      rowTop: outgoingRow.top,
      rowHeight: outgoingRow.height,
      viewportTop: outgoingViewportTop,
      isVisible: isOutgoingVisible,
    };

    const incoming: SplitIntersectionIndicator = {
      paneIndex: targetPane.paneIndex,
      targetPaneIndex: sourcePane.paneIndex,
      role: "incoming",
      rowIndex: incomingRow.index,
      pairId,
      colorIndex,
      label: `Continued from Col ${sourcePane.paneIndex + 1}`,
      rowTop: incomingRow.top,
      rowHeight: incomingRow.height,
      viewportTop: incomingViewportTop,
      isVisible: isIncomingVisible,
    };

    pairs.push({
      id: pairId,
      sourcePaneIndex: sourcePane.paneIndex,
      targetPaneIndex: targetPane.paneIndex,
      colorIndex,
      outgoing,
      incoming,
    });
  }

  return pairs;
}

/**
 * Calculates flat array of split intersection indicators for all active panes.
 */
export function calculateSplitIntersections(
  panes: PaneMetrics[],
  rows: RowMetrics[],
  splitMode: SplitMode
): SplitIntersectionIndicator[] {
  const pairs = calculateSplitIntersectionPairs(panes, rows, splitMode);
  return pairs.flatMap((pair) => [pair.outgoing, pair.incoming]);
}

/**
 * Extracts PaneMetrics from a DOM element.
 */
export function measurePaneMetrics(
  el: HTMLElement,
  index: number,
  pageNumber?: number
): PaneMetrics {
  const parsedPage =
    pageNumber !== undefined
      ? pageNumber
      : el.hasAttribute("data-page-number")
      ? Number(el.getAttribute("data-page-number"))
      : el.hasAttribute("data-split-page")
      ? Number(el.getAttribute("data-split-page"))
      : undefined;

  return {
    paneIndex: index,
    scrollTop: el.scrollTop || 0,
    clientHeight: el.clientHeight || 0,
    scrollHeight: el.scrollHeight || 0,
    pageNumber: Number.isFinite(parsedPage) ? parsedPage : undefined,
  };
}

/**
 * Extracts RowMetrics from a container element measuring its child rows.
 */
export function measureRowMetrics(container: HTMLElement): RowMetrics[] {
  if (!container) return [];

  let rowElements = Array.from(
    container.querySelectorAll<HTMLElement>("[data-testid='song-row'], .song-row")
  );

  if (rowElements.length === 0) {
    const parentContainer =
      container.firstElementChild instanceof HTMLElement &&
      container.firstElementChild.children.length > 0
        ? container.firstElementChild
        : container;
    rowElements = Array.from(parentContainer.children).filter(
      (child): child is HTMLElement => child instanceof HTMLElement
    );
  }

  if (rowElements.length === 0) return [];

  const metrics: RowMetrics[] = [];
  const containerScrollTop = container.scrollTop || 0;
  let cumulativeTop = 0;

  rowElements.forEach((el, index) => {
    let top = el.offsetTop;
    let height = el.offsetHeight;

    if ((!height || height <= 0) && el.hasAttribute("data-row-height")) {
      height = Number(el.getAttribute("data-row-height"));
    }
    if ((top === undefined || top === 0) && el.hasAttribute("data-row-top")) {
      top = Number(el.getAttribute("data-row-top"));
    }

    if (
      typeof el.getBoundingClientRect === "function" &&
      typeof container.getBoundingClientRect === "function"
    ) {
      const elRect = el.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();
      if (elRect.height > 0) {
        height = elRect.height;
        top = elRect.top - containerRect.top + containerScrollTop;
      }
    }

    if (top === undefined || (top === 0 && index > 0)) {
      top = cumulativeTop;
    }

    const effectiveHeight = height > 0 ? height : 32;
    const effectiveTop = top !== undefined ? top : cumulativeTop;
    const bottom = effectiveTop + effectiveHeight;

    metrics.push({
      index,
      top: effectiveTop,
      height: effectiveHeight,
      bottom,
    });

    cumulativeTop = bottom;
  });

  return metrics;
}

/**
 * Computes split intersection indicators directly from DOM pane elements.
 */
export function computeDOMSplitIntersections(
  paneElements: (HTMLElement | null)[],
  splitMode: SplitMode,
  rows?: RowMetrics[]
): SplitIntersectionIndicator[] {
  if (splitMode <= 1 || !paneElements || paneElements.length < 2) {
    return [];
  }

  let firstPaneEl: HTMLElement | null = null;
  paneElements.forEach((el, index) => {
    if (el && index < splitMode && !firstPaneEl) {
      firstPaneEl = el;
    }
  });

  const measuredRows =
    rows && rows.length > 0
      ? rows
      : firstPaneEl
      ? measureRowMetrics(firstPaneEl)
      : [];

  if (measuredRows.length === 0) {
    return [];
  }

  const maxContentHeight = measuredRows[measuredRows.length - 1].bottom;
  const validPanes: PaneMetrics[] = [];

  paneElements.forEach((el, index) => {
    if (el && index < splitMode) {
      const metrics = measurePaneMetrics(el, index);
      if (metrics.clientHeight <= 0) {
        metrics.clientHeight =
          typeof window !== "undefined" && window.innerHeight
            ? Math.round(window.innerHeight * 0.75)
            : 600;
      }
      if (
        metrics.scrollTop === 0 &&
        metrics.pageNumber &&
        metrics.pageNumber > 1
      ) {
        const effectivePageHeight = Math.min(
          metrics.clientHeight,
          Math.max(100, Math.ceil(maxContentHeight / splitMode) + 42)
        );
        const step = Math.max(1, effectivePageHeight - 42);
        metrics.scrollTop = Math.min(
          (metrics.pageNumber - 1) * step,
          Math.max(0, maxContentHeight - effectivePageHeight + 42)
        );
        metrics.clientHeight = effectivePageHeight;
      }
      validPanes.push(metrics);
    }
  });

  if (validPanes.length < 2) {
    return [];
  }

  return calculateSplitIntersections(validPanes, measuredRows, splitMode);
}
